"use client";

import { setVoyadoContactId } from "@/utils/voyadoTracking";
import mergeGuestWishlistOnce, {
  clearGuestWishlistMergeState,
} from "@features/account/utils/mergeGuestWishlist";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Auth, User } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  REAUTH_SUCCESS_STORAGE_KEY,
  RECOVERY_ATTEMPTS_STORAGE_KEY,
} from "../const";
import clearAuthCookie from "../services/clearAuthCookie";
import establishSession, {
  clearSessionEstablishment,
} from "../services/establishSession";
import isAuthError from "../utils/isAuthError";

type UseRefreshTokenArgs = {
  auth: Auth | undefined;
  serviceLocale: string | undefined;
};

const MAX_TRANSIENT_RETRIES = 2;

export default function useRefreshToken({
  auth,
  serviceLocale,
}: UseRefreshTokenArgs) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [initialized, setInitialized] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const transientRetryRef = useRef(0);
  const router = useRouter();
  const queryClient = useQueryClient();

  const clearRecoveryState = useCallback(() => {
    sessionStorage.removeItem(REAUTH_SUCCESS_STORAGE_KEY);
    sessionStorage.removeItem(RECOVERY_ATTEMPTS_STORAGE_KEY);
  }, []);

  const markReauthSuccess = useCallback(() => {
    sessionStorage.setItem(REAUTH_SUCCESS_STORAGE_KEY, String(Date.now()));
  }, []);

  const {
    mutate,
    mutateAsync: authenticate,
    isPending: refreshing,
  } = useMutation({
    mutationFn: async (authUser: User) => {
      const session = await establishSession(authUser, serviceLocale);
      return { authUser, ...session };
    },
    onSuccess: ({ authUser, token: sessionToken, reauthenticated }) => {
      transientRetryRef.current = 0;
      setAuthFailed(false);
      setUser(authUser);
      setToken(sessionToken);
      setVoyadoContactId(sessionToken);

      if (serviceLocale) {
        void mergeGuestWishlistOnce({
          uid: authUser.uid,
          token: sessionToken,
          locale: serviceLocale,
        }).then(() => {
          void queryClient.invalidateQueries({ queryKey: ["wishlist"] });
          void queryClient.invalidateQueries({ queryKey: ["wishlistItems"] });
        });
      }

      if (!reauthenticated) {
        clearRecoveryState();
      }

      if (reauthenticated && window.location.pathname.includes("/account")) {
        markReauthSuccess();
        router.refresh();
      }
    },
    onError: async (error, authUser) => {
      console.warn("[auth] session refresh failed:", error);

      if (!isAuthError(error)) {
        if (transientRetryRef.current < MAX_TRANSIENT_RETRIES) {
          transientRetryRef.current += 1;
          const delayMs = 1000 * transientRetryRef.current;
          window.setTimeout(() => {
            mutate(authUser);
          }, delayMs);
          return;
        }

        // Keep the Firebase user so recovery can retry, but do not treat
        // this as an authenticated session until a cookie exists.
        setUser(authUser);
        setToken(undefined);
        setAuthFailed(true);
        return;
      }

      transientRetryRef.current = 0;
      setAuthFailed(true);
      setUser(null);
      setToken(undefined);
      clearSessionEstablishment();
      await clearAuthCookie();

      if (auth) {
        const { signOut } = await import("firebase/auth");
        await signOut(auth);
      }
    },
  });

  useEffect(() => {
    if (!auth) {
      setInitialized(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    import("firebase/auth").then(({ onIdTokenChanged }) => {
      if (cancelled) return;

      unsubscribe = onIdTokenChanged(auth, (authUser) => {
        setInitialized(true);

        if (authUser) {
          setAuthFailed(false);
          mutate(authUser);
        } else {
          setUser(null);
          setToken(undefined);
          setAuthFailed(false);
          clearSessionEstablishment();
          clearGuestWishlistMergeState();
          void clearAuthCookie();
        }
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [auth, mutate]);

  return {
    user,
    token,
    authenticated: Boolean(user && token),
    authenticate,
    initializing: !initialized,
    refreshing,
    loading:
      refreshing ||
      !initialized ||
      (!!auth?.currentUser && !token && !authFailed),
  };
}
